import React, { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Autocomplete,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { extractErrorMessage } from '../../../utils/extractErrorMessage'
import { useSnackbar } from 'notistack'
import { dateRequired, positiveIntegerRequired } from '../yup-helper'

const typeOptions = ['CREDIT', 'DEBIT']

const schema = yup.object({
  mapId: positiveIntegerRequired('Map ID'),
  type: yup
    .string()
    .oneOf(typeOptions, 'Type must be CREDIT or DEBIT')
    .required('Type is required'),
  transactionDate: dateRequired('Transaction Date'),
  amount: yup.number().positive('Amount must be greater than 0').required('Amount is required'),
  description: yup
    .string()
    .required('Description is required')
    .max(255, 'Description must be at most 255 characters'),
})

const Form = ({ defaultValues, setOpen, open, api, refetch, isCreateMode }) => {
  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
  })

  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync, isLoading } = api()

  const onClose = () => setOpen(false)

  const onSubmit = async (values) => {
    try {
      await mutateAsync(values)
      refetch()
      enqueueSnackbar(`Successfully ${isCreateMode ? 'created' : 'updated'}`, {
        variant: 'success',
      })
      onClose()
      reset({ ...defaultValues })
    } catch (e) {
      enqueueSnackbar(extractErrorMessage(e), { variant: 'error' })
    }
  }

  useEffect(() => reset({ ...defaultValues }), [defaultValues, reset])

  return (
    <Dialog maxWidth='sm' fullWidth open={open}>
      <DialogTitle>{isCreateMode ? 'Create Transaction' : 'Edit Transaction'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='transactionDate'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label='Transaction Date'
                  variant='outlined'
                  fullWidth
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='description'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label='Description'
                  variant='outlined'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='type'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  options={typeOptions}
                  value={field.value || ''}
                  onChange={(_, v) => field.onChange(v || '')}
                  onBlur={field.onBlur}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Type'
                      variant='outlined'
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name='amount'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label='Amount'
                  variant='outlined'
                  fullWidth
                  type='number'
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit(onSubmit)} disabled={isLoading}>
          {isCreateMode ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default Form
